<script setup lang="ts">
import { useHead, useSeoMeta } from '@unhead/vue'
import { useMediaQuery } from '@vueuse/core'
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import { ScalarSidebarFooter, useModal } from '@scalar/components'
import { type OpenAPIV3_1 } from '@scalar/types/legacy'
import { useColorMode } from '@scalar/use-hooks/useColorMode'
import {
  type ApiReferenceConfiguration,
  ApiReferencePro,
  SearchButton,
  SearchModal,
} from '@scalar-org/api-reference-pro'
import { customFooterClass } from '@scalar-org/css'
import {
  DocumentHeader,
  DocumentMobileHeader,
} from '@scalar-org/guide/components'
import { normalizeRefVersion } from '@scalar-org/helpers/normalize'

import ScalarFreePromo from '@/components/ScalarFreePromo.vue'
import { resolveHeaderLink } from '@/helpers/resolve-link'
import { useSidebarState } from '@/hooks'
import ReferenceDocumentSelector from '@/views/references/ReferenceDocumentSelector.vue'
import ReferenceNavWatcher from '@/views/references/ReferenceNavWatcher.vue'
import { type PublishReferenceProps } from '@/views/references/types'

const props = defineProps<
  PublishReferenceProps & {
    parsed: OpenAPIV3_1.Document
    configuration: ApiReferenceConfiguration
  }
>()

const { isDarkMode, toggleColorMode } = useColorMode()

useSeoMeta({
  title: () =>
    props.parsed.info?.title ??
    props.project.website.title ??
    'API Documentation',
  // TODO: Consider if we want to override the description
  // description: props.page.description ?? props.guide.description,
})
useHead({
  script: [
    {
      tagPosition: 'bodyClose',
      textContent: () => props.version.bodyScript,
    },
    {
      tagPosition: 'head',
      textContent: () => props.version.headScript,
    },
  ],
  style: [
    {
      tagPosition: 'head',
      textContent: () => props.version.footerCss,
    },
  ],
})

const { sidebarState, toggleSidebar, setSidebar } = useSidebarState()

const router = useRouter()

// ---------------------------------------------------------------------------

const versions = computed(() => {
  const reference = props.version.references[props.referenceUid]

  if (!reference || !reference.managedDoc) return []

  return reference.managedDoc.versions
})

const selectedVersion = computed({
  get: () => normalizeRefVersion(props.referenceVersion).version,
  set: (v) => {
    const { uid } = normalizeRefVersion(props.referenceUid, v)

    router.push({
      name: uid,
    })
  },
})

const isMobile = useMediaQuery('(max-width: 1000px)')

const footerSlot = computed(() =>
  props.version.footerBelowSidebar ? 'footer' : 'content-end',
)

const searchModalState = useModal()
</script>
<template>
  <div class="api-references-layout">
    <ApiReferencePro
      class="references-sidebar-mobile-open"
      :configuration
      :isDark="isDarkMode"
      :uid="props.referenceUid"
      @toggleDarkMode="toggleColorMode">
      <template #header="{ breadcrumb }">
        <DocumentMobileHeader
          v-if="isMobile"
          :label="breadcrumb"
          :showSideBar="sidebarState.showSidebar"
          @showSearch="searchModalState.show()"
          @toggleSidebar="toggleSidebar" />
        <DocumentHeader
          v-else
          :activeGuide="undefined"
          :activeVersion="version.uid"
          :headerLinks="headerLinks"
          :project="project"
          :resolveInternalLinks="(item) => resolveHeaderLink(item, router)"
          :versions="versionNames"
          @navigate="(_, uid) => router.push({ name: uid })"
          @selectVersion="(uid) => router.push({ name: uid })"
          @setSidebar="setSidebar" />
        <SearchModal
          :hideModels="false"
          :modalState="searchModalState" />
        <ReferenceNavWatcher :breadcrumb />
      </template>
      <template #sidebar-start>
        <DocumentHeader
          v-if="isMobile"
          :activeGuide="undefined"
          :activeVersion="version.uid"
          :headerLinks="headerLinks"
          :project="project"
          :resolveInternalLinks="(item) => resolveHeaderLink(item, router)"
          :versions="versionNames"
          @navigate="(_, uid) => router.push({ name: uid })"
          @selectVersion="(uid) => router.push({ name: uid })"
          @setSidebar="setSidebar" />
        <div
          v-else
          class="api-references-search">
          <ReferenceDocumentSelector
            v-model="selectedVersion"
            :versions="versions" />
          <SearchButton />
        </div>
      </template>
      <template
        v-if="!isMobile"
        #sidebar-end>
        <ScalarSidebarFooter>
          <template #description>
            <ScalarFreePromo v-if="!isPaid" />
            <div v-else></div>
          </template>
        </ScalarSidebarFooter>
      </template>
      <template #[footerSlot]>
        <div
          class="references-footer"
          :class="customFooterClass"
          v-html="version.footer" />
      </template>
    </ApiReferencePro>
  </div>
</template>
<style scoped>
.api-references-layout {
  flex: 1;
  overflow-y: auto;
}

.api-references-search {
  padding: 12px 12px 6px 12px;
}
</style>
